<template>
  <div>
    <template v-if="dense">
      <div class="row q-gutter-x-md">
        <div class="col">
          <q-list class="basic-info-list">
            <q-item v-if="logArea" dense>
              <q-item-section>
                <q-item-label><span>{{ $t('logArea') }}</span></q-item-label>
                <q-item-label>{{ logArea | area(unitLabel('sqm'), userLocale) }}</q-item-label>
              </q-item-section>
            </q-item>
            <q-item v-if="palletInfo && user && !isTableTop" dense>
              <q-item-section>
                <q-item-label><span>{{ $t('palletInfo') }}</span></q-item-label>
                <q-item-label>{{ palletInfo }}</q-item-label>
              </q-item-section>
            </q-item>
            <q-item v-if="user && !simplified && logInfo && isTableTop" dense>
              <q-item-section>
                <q-item-label><span>{{ $t('logInfo') }}</span></q-item-label>
                <q-item-label>{{ logInfo }}</q-item-label>
              </q-item-section>
            </q-item>
            <q-item v-if="thicknessMm && !isDefaultThickness" dense>
              <q-item-section>
                <q-item-label><span>{{ $t('thickness') }}</span></q-item-label>
                <q-item-label>{{ thicknessMm | numberWithUnit('mm', userLocale) }}</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </div>
        <div class="col">
          <q-list class="basic-info-list">
            <q-item v-if="maxLength" dense>
              <q-item-section>
                <q-item-label><span>{{ $t('maxLength') }}</span></q-item-label>
                <q-item-label>{{ maxLength | numberWithUnit('cm', userLocale) }}</q-item-label>
              </q-item-section>
            </q-item>
            <q-item v-if="grade && user && !simplified" dense>
              <q-item-section>
                <q-item-label><span>{{ $t('grade') }}</span></q-item-label>
                <q-item-label>{{ grade | encodeGrade }}</q-item-label>
              </q-item-section>
            </q-item>
            <q-item v-if="internalQualityName && user && !isTableTop" dense>
              <q-item-section>
                <q-item-label><span>{{ $t('internalQuality') }}</span></q-item-label>
                <q-item-label>{{ internalQualityName }}</q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </div>
      </div>

      <q-slide-transition>
        <div v-if="!collapsed">
          <q-separator spaced/>
          <div class="row q-gutter-x-md">
            <div class="col">
              <q-list class="basic-info-list">
                <q-item v-if="customerQualityName && !simplified" dense>
                  <q-item-section>
                    <q-item-label><span>{{ $t('quality') }}</span></q-item-label>
                    <q-item-label>{{ customerQualityName }}</q-item-label>
                  </q-item-section>
                </q-item>
                <q-item v-if="place && user && !simplified" dense>
                  <q-item-section>
                    <q-item-label><span>{{ $t('place') }}</span></q-item-label>
                    <q-item-label>{{ place }}</q-item-label>
                  </q-item-section>
                </q-item>
                <q-item v-if="user && !simplified && logInfo && !isTableTop" dense>
                  <q-item-section>
                    <q-item-label><span>{{ $t('logInfo') }}</span></q-item-label>
                    <q-item-label>{{ logInfo }}</q-item-label>
                  </q-item-section>
                </q-item>
                <q-item v-if="user && palletInfo && isTableTop" dense>
                  <q-item-section>
                    <q-item-label><span>{{ $t('palletInfo') }}</span></q-item-label>
                    <q-item-label>{{ palletInfo }}</q-item-label>
                  </q-item-section>
                </q-item>
                <q-item
                    v-if="resultAreaNotRemeasured && (logSearchParams.lengthFrom || logSearchParams.widthFrom || logSearchParams.widthTo)"
                    dense>
                  <q-item-section>
                    <q-item-label><span>{{ $t('packageArea') }}</span></q-item-label>
                    <q-item-label>{{ resultAreaNotRemeasured | area(unitLabel('sqm'), userLocale) }}</q-item-label>
                  </q-item-section>
                </q-item>
                <q-item
                    v-if="resultAreaNotRemeasured && logArea && (logSearchParams.lengthFrom || logSearchParams.widthFrom || logSearchParams.widthTo)"
                    dense>
                  <q-item-section>
                    <q-item-label><span>{{ $t('inLogPercentage') }}</span></q-item-label>
                    <q-item-label>{{
                        (resultAreaNotRemeasured / logArea) * 100 | numberWithUnit(unitLabel('percentage'), userLocale, 0, 0)
                      }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </div>
            <div class="col">
              <q-list class="basic-info-list">
                <q-item v-if="structure && !simplified" dense>
                  <q-item-section>
                    <q-item-label><span>{{ $t('structure') }}</span></q-item-label>
                    <q-item-label>{{ structure }}</q-item-label>
                  </q-item-section>
                </q-item>
                <q-item v-if="palletNo && user && !simplified" dense>
                  <q-item-section>
                    <q-item-label><span>{{ $t('palletNo') }}</span></q-item-label>
                    <q-item-label>{{ palletNo }}</q-item-label>
                  </q-item-section>
                </q-item>
                <q-item v-if="characteristic" dense>
                  <q-item-section>
                    <q-item-label><span>{{ $t('characteristic') }}</span></q-item-label>
                    <q-item-label>{{ characteristic }}</q-item-label>
                  </q-item-section>
                </q-item>
                <q-item v-if="resultAreaMeasuredBack && logSearchParams.lengthMax" dense>
                  <q-item-section>
                    <q-item-label><span>{{ $t('measuredArea') }} ({{ $t('unit.sqm') }})</span></q-item-label>
                    <q-item-label>{{
                        resultAreaMeasuredBack | numberWithUnit(unitLabel('sqm'), userLocale)
                      }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
                <q-item v-if="resultAreaMeasuredBack && resultAreaNotRemeasured && logSearchParams.lengthMax" dense>
                  <q-item-section>
                    <q-item-label><span>{{ $t('measuredArea') }} ({{ $t('unit.percentage') }})</span></q-item-label>
                    <q-item-label>{{
                        (resultAreaMeasuredBack / resultAreaNotRemeasured) * 100 | numberWithUnit(unitLabel('percentage'), userLocale, 0, 0)
                      }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </div>
          </div>

          <slot/>
        </div>
      </q-slide-transition>

      <q-btn flat color="primary" class="card-collapse-button" @click="collapsed = !collapsed" :icon="collapsed ? 'add' : 'remove'"
             :label="collapsed ? $t('more') : $t('less')" padding="4px 4px 4px 0" no-caps/>

    </template>
    <template v-else>
      <ul class="merged-info-list">
        <li v-if="logArea"><span>{{ $t('logArea') }}</span> {{ logArea | area(unitLabel('sqm'), userLocale) }}</li>
        <li v-if="resultAreaNotRemeasured && (logSearchParams.lengthFrom || logSearchParams.widthFrom || logSearchParams.widthTo)">
          <span>{{ $t('packageArea') }}</span> {{ resultAreaNotRemeasured | area(unitLabel('sqm'), userLocale) }}
        </li>
        <li v-if="resultAreaNotRemeasured && logArea && (logSearchParams.lengthFrom || logSearchParams.widthFrom || logSearchParams.widthTo)">
          <span>{{ $t('inLogPercentage') }}</span>
          {{ (resultAreaNotRemeasured / logArea) * 100 | numberWithUnit(unitLabel('percentage'), userLocale, 0, 2) }}
        </li>
        <li v-if="resultAreaMeasuredBack && logSearchParams.lengthMax">
          <span>{{ $t('measuredArea') }} ({{ $t('unit.sqm') }})</span>
          {{ resultAreaMeasuredBack | numberWithUnit(unitLabel('sqm'), userLocale) }}
        </li>
        <li v-if="resultAreaMeasuredBack && resultAreaNotRemeasured && logSearchParams.lengthMax">
          <span>{{ $t('measuredArea') }} ({{ $t('unit.percentage') }})</span> {{
            (resultAreaMeasuredBack / resultAreaNotRemeasured) * 100 | numberWithUnit(unitLabel('percentage'), userLocale, 0, 2)
          }}
        </li>
        <li v-if="maxLength"><span>{{ $t('maxLength') }}</span> {{ maxLength | numberWithUnit('cm', userLocale) }}</li>
        <li v-if="customerQualityName && !simplified"><span>{{ $t('quality') }}</span> {{ customerQualityName }}</li>
        <li v-if="structure && !simplified"><span>{{ $t('structure') }}</span> {{ structure }}</li>
        <li v-if="grade && user && !simplified"><span>{{ $t('grade') }}</span> {{ grade | encodeGrade }}</li>
        <li v-if="palletNo && user && !simplified"><span>{{ $t('palletNo') }}</span> {{ palletNo }}</li>
        <li v-if="place && user && !simplified"><span>{{ $t('place') }}</span> {{ place }}</li>
        <li v-if="characteristic"><span>{{ $t('characteristic') }}</span> {{ characteristic }}</li>
        <li v-if="thicknessMm && !isDefaultThickness"><span>{{ $t('thickness') }}</span>
          {{ thicknessMm | numberWithUnit('mm', userLocale) }}
        </li>
        <li v-if="fillLastRow"></li>
        <li class="list-break"></li>
        <li v-if="internalQualityName && user && !simplified"><span>{{ $t('internalQuality') }}</span>
          {{ internalQualityName }}
        </li>
        <li v-if="logInfo && user && !simplified"><span>{{ $t('logInfo') }}</span> {{ logInfo }}</li>
        <li v-if="palletInfo && user && !simplified"><span>{{ $t('palletInfo') }}</span> {{ palletInfo }}</li>
      </ul>
    </template>
  </div>
</template>

<script>
import LogPropertiesResolver from "@/mixins/LogPropertiesResolver";
import NumberFormattingFilters from "@/mixins/NumberFormattingFilters";
import GradeFilter from "@/mixins/GradeFilter";
import {mapGetters} from "vuex";
import {defaultThickness} from "@/common/config";

export default {
  name: "VeneerLogInfo",
  mixins: [LogPropertiesResolver, NumberFormattingFilters, GradeFilter],
  props: {
    log: Object,
    dense: {
      type: Boolean,
      default: false
    },
    isPublic: {
      type: Boolean,
      default: false
    },
    virtual: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      collapsed: true
    }
  },
  computed: {
    ...mapGetters(['user', 'watchlist']),
    simplified() {
      return this.watchlist && this.isPublic ? this.watchlist.simplified : false;
    },
    isDefaultThickness() {
      return this.thicknessMm === defaultThickness;
    },
    fillLastRow() {
      let left = 0,
          right = 0;

      if (this.logArea) left++;
      if (this.customerQualityName && !this.simplified) left++;
      if (this.place && this.user && !this.simplified) left++;
      if (this.grade && this.user && !this.simplified) left++;
      if (this.thicknessMm && !this.isDefaultThickness) left++

      if (this.maxLength) right++;
      if (this.structure && !this.simplified) right++;
      if (this.palletNo && this.user && !this.simplified) right++;
      if (this.characteristic) right++;

      return right < left;
    }
  }
}
</script>

<style lang="scss">
.card-collapse-button {
  margin: 5px 0 5px -5px;

  .on-left {
    margin-right: 4px;
  }
}

.basic-info-list {
  .q-item {
    padding: 0;
    margin-bottom: 8px;

    span {
      color: var(--q-color-secondary);
      word-break: break-word;
    }
  }
}

.extended-info-list .basic-info-list .q-item {
  margin-bottom: 0;
}

.merged-info-list {
  margin: 0;
  padding: 0;

  li {
    display: inline-block;
    border-left: 1px solid;
    padding: 0 0 0 1rem;
    margin: 0.5rem 0 0.5rem 1rem;

    &.list-break {
      display: block;
      width: 0;
      margin: 0;
      padding: 0;
    }

    &:first-child,
    &.list-break + li {
      border: none;
      padding-left: 0;
      margin-left: 0;
    }

    span {
      margin-right: 1rem;
    }
  }
}

@media(max-width: 900px) {
  .basic-info-list {
    span {
      width: 80px;
    }
  }
}

@media(max-width: 600px) {
  .basic-info-list {
    span {
      width: 60px;
    }
  }
}
</style>